import { Button, Textarea } from '@monksflow/monks-ui';
import { Trash2Icon } from 'lucide-react';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PersonaFieldLabel } from './PersonaFieldLabel';

interface RepeaterColumn {
  /** Sub-field under each row, e.g. `item` or `description`. */
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  /** Tailwind width for the column; the last one usually grows. */
  className?: string;
  rows?: number;
}

interface PersonaRepeaterRowsProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  /** The field-array name, e.g. `caresAbout`. */
  name: string;
  fields: { id: string }[];
  columns: RepeaterColumn[];
  onRemove: (index: number) => void;
}

/**
 * The repeated-row editor shared by every list section of the persona form —
 * cares about, pains, gains, biography items.
 *
 * The V1 sections each rebuild this grid by hand; collapsing them onto one
 * component is what keeps the four from drifting apart as they are restyled.
 */
export function PersonaRepeaterRows<TFieldValues extends FieldValues>({
  control,
  name,
  fields,
  columns,
  onRemove,
}: PersonaRepeaterRowsProps<TFieldValues>) {
  const { t } = useTranslation('audiences');

  // The last row is the section's only content; removing it would leave nothing
  // to describe the persona with.
  const canRemove = fields.length > 1;

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex items-center gap-sm">
        {columns.map((column) => (
          <div key={column.name} className={column.className ?? 'flex-1'}>
            <PersonaFieldLabel required={column.required}>{column.label}</PersonaFieldLabel>
          </div>
        ))}
        <div className="w-9" aria-hidden />
      </div>

      {fields.map((field, index) => (
        <div key={field.id} className="flex items-start gap-sm">
          {columns.map((column) => (
            <div key={column.name} className={column.className ?? 'flex-1'}>
              <Controller
                name={`${name}.${index}.${column.name}` as Path<TFieldValues>}
                control={control}
                render={({ field: rowField, fieldState: { error } }) => (
                  <>
                    <Textarea
                      {...rowField}
                      placeholder={column.placeholder ?? column.label}
                      rows={column.rows ?? 2}
                      aria-label={column.label}
                      aria-invalid={Boolean(error) || undefined}
                    />
                    {error?.message && (
                      <p className="mt-3xs text-xs text-destructive">{error.message}</p>
                    )}
                  </>
                )}
              />
            </div>
          ))}
          <Button
            variant="ghost"
            size="mini"
            className="mt-1 text-destructive"
            disabled={!canRemove}
            aria-label={t('audiences.personaRepeaterRows.removeRow', { row: index + 1 })}
            onClick={() => onRemove(index)}
          >
            <Trash2Icon className="size-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}

PersonaRepeaterRows.displayName = 'PersonaRepeaterRows';
